import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';

const getFromTo = (dateRange, customStart, customEnd) => {
  const now = new Date();
  let from = new Date();
  let to = new Date();
  if (dateRange === '7d') {
    from.setDate(now.getDate() - 6);
  } else if (dateRange === '30d') {
    from.setDate(now.getDate() - 29);
  } else if (dateRange === 'custom' && customStart && customEnd) {
    from = new Date(customStart);
    to = new Date(customEnd);
  } else {
    return null;
  }
  from.setHours(0, 0, 0, 0);
  to.setHours(23, 59, 59, 999);
  const fmt = (d) => d.toISOString().split('.')[0];
  return { from: fmt(from), to: fmt(to) };
};

const DateRangePicker = ({ dateRange, setDateRange, customStart, setCustomStart, customEnd, setCustomEnd }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref?.current && !ref?.current?.contains(e?.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const options = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: 'custom', label: 'Custom' },
  ];

  const getLabel = () => {
    if (dateRange === '7d') return 'Last 7 days';
    if (dateRange === '30d') return 'Last 30 days';
    if (dateRange === 'custom' && customStart && customEnd) return `${customStart} → ${customEnd}`;
    return 'Custom';
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-lg text-xs font-medium text-foreground hover:bg-muted transition-colors"
      >
        <Icon name="Calendar" size={13} color="#6B7280" />
        <span>{getLabel()}</span>
        <Icon name="ChevronDown" size={13} color="#6B7280" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 bg-card border border-border rounded-xl shadow-lg p-2 min-w-[220px]">
          {options?.map((opt) => (
            <button
              key={opt?.value}
              onClick={() => {
                setDateRange(opt?.value);
                if (opt?.value !== 'custom') setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                dateRange === opt?.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-muted'
              }`}
            >
              {opt?.label}
            </button>
          ))}

          {dateRange === 'custom' && (
            <div className="mt-2 pt-2 border-t border-border space-y-2 px-1">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Start Date</label>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e?.target?.value)}
                  className="w-full px-2 py-1.5 text-xs border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">End Date</label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e?.target?.value)}
                  min={customStart}
                  className="w-full px-2 py-1.5 text-xs border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <button
                onClick={() => { if (customStart && customEnd) setOpen(false); }}
                disabled={!customStart || !customEnd}
                className="w-full py-1.5 rounded-lg text-xs font-medium bg-primary text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const f2 = (v) => parseFloat(Number(v).toFixed(2));

const NPSKPICards = ({ onDataLoaded, onDateRangeChange }) => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [dateRange, setDateRange] = useState('7d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const range = getFromTo(dateRange, customStart, customEnd);
    if (!range) return;
    if (onDateRangeChange) onDateRangeChange(range);
    setLoading(true);
    api.get('/api/v1/nps-analytics/kpis', { params: range })
      .then((res) => {
        const d = res.data?.data || res.data;
        setData(d);
        if (onDataLoaded) onDataLoaded(d);
      })
      .catch((err) => console.error('Failed to fetch NPS KPIs:', err))
      .finally(() => setLoading(false));
  }, [dateRange, customStart, customEnd]);

  const npsScore = data?.npsScore ?? 0;
  const promoters = data?.promotersPercent ?? 0;
  const passives = data?.passivesPercent ?? 0;
  const detractors = data?.detractorsPercent ?? 0;
  const promotersVolume = data?.promoters ?? 0;
  const passivesVolume = data?.passives ?? 0;
  const detractorsVolume = data?.detractors ?? 0;
  const totalCallsMade = data?.totalCallsMade ?? 0;
  const totalCallsConnected = data?.totalCallsConnected ?? 0;
  const totalResponses = data?.totalResponses ?? 0;
  const responseRate = data?.responseRate ?? 0;
  const previousNPS = data?.previousNPS ?? 0;

  const npsChange = data?.npsChange ?? 0;
  const isPositive = npsChange >= 0;

  const getNPSColor = (score) => {
    if (score >= 50) return '#059669';
    if (score >= 0) return '#D97706';
    return '#DC2626';
  };

  const npsColor = getNPSColor(npsScore);

  return (
    <div className="mb-6">
      {/* Date Range Picker Header */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-muted-foreground font-medium">KPI Overview</p>
        <DateRangePicker
          dateRange={dateRange}
          setDateRange={setDateRange}
          customStart={customStart}
          setCustomStart={setCustomStart}
          customEnd={customEnd}
          setCustomEnd={setCustomEnd}
        />
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {!loading && <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* NPS Score - Primary Large Card */}
        <div
          className="relative bg-card border border-border rounded-xl p-6 cursor-pointer overflow-hidden"
          style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
          onMouseEnter={() => setHoveredCard('nps')}
          onMouseLeave={() => setHoveredCard(null)}
        >
          <div className="absolute top-0 left-0 w-1 h-full rounded-l-xl" style={{ background: npsColor }} />
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">NPS Score</p>
              <div className="flex items-end gap-2">
                <span className="text-5xl font-bold data-text" style={{ color: npsColor }}>{f2(npsScore)}</span>
              </div>
            </div>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${npsColor}15` }}>
              <Icon name="TrendingUp" size={20} color={npsColor} />
            </div>
          </div>

          <div className="flex items-center gap-1 mb-4">
            <Icon name={isPositive ? 'ArrowUpRight' : 'ArrowDownRight'} size={14} color={isPositive ? '#059669' : '#DC2626'} />
            <span className="text-xs font-medium" style={{ color: isPositive ? '#059669' : '#DC2626' }}>
              {isPositive ? '+' : ''}{f2(npsChange)} vs previous period
            </span>
          </div>

          {/* Hover breakdown */}
          <div className={`transition-all duration-300 ${hoveredCard === 'nps' ? 'opacity-100 max-h-24' : 'opacity-0 max-h-0 overflow-hidden'}`}>
            <div className="border-t border-border pt-3 grid grid-cols-3 gap-2">
              <div className="text-center">
                <div className="text-sm font-bold text-green-600">{f2(promoters)}%</div>
                <div className="text-xs text-muted-foreground">Promoters</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-bold text-yellow-600">{f2(passives)}%</div>
                <div className="text-xs text-muted-foreground">Passives</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-bold text-red-600">{f2(detractors)}%</div>
                <div className="text-xs text-muted-foreground">Detractors</div>
              </div>
            </div>
          </div>

          {hoveredCard !== 'nps' && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-green-600 font-medium">Promoters</span>
                <span className="text-green-600 font-bold">{f2(promoters)}%</span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden flex">
                <div className="h-full bg-green-500 rounded-l-full" style={{ width: `${promoters}%` }} />
                <div className="h-full bg-yellow-400" style={{ width: `${passives}%` }} />
                <div className="h-full bg-red-500 rounded-r-full" style={{ width: `${detractors}%` }} />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{f2(passives)}% Passives</span>
                <span className="text-red-500">{f2(detractors)}% Detractors</span>
              </div>
            </div>
          )}
        </div>

        {/* Response Distribution */}
        <div
          className="bg-card border border-border rounded-xl p-6 cursor-pointer"
          style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
          onMouseEnter={() => setHoveredCard('dist')}
          onMouseLeave={() => setHoveredCard(null)}
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Response Distribution</p>
              <p className="text-2xl font-bold text-foreground data-text">{totalResponses?.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Total responses</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Icon name="PieChart" size={20} color="#1E3A8A" />
            </div>
          </div>
          <div className="space-y-2">
            {[
              { label: 'Promoters (9–10)', pct: promoters, vol: promotersVolume, color: '#059669', bg: '#d1fae5' },
              { label: 'Passives (7–8)', pct: passives, vol: passivesVolume, color: '#D97706', bg: '#fef3c7' },
              { label: 'Detractors (0–6)', pct: detractors, vol: detractorsVolume, color: '#DC2626', bg: '#fee2e2' },
            ]?.map((item) => (
              <div key={item?.label} className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: item?.color }} />
                <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: item?.bg }}>
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${item?.pct}%`, background: item?.color }} />
                </div>
                <span className="text-xs font-medium data-text w-8 text-right" style={{ color: item?.color }}>{item?.vol}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Total Calls vs Responses */}
        <div
          className="bg-card border border-border rounded-xl p-6"
          style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Calls Funnel</p>
              <p className="text-2xl font-bold text-foreground data-text">{totalCallsMade?.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Total calls initiated</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
              <Icon name="Phone" size={20} color="#0F766E" />
            </div>
          </div>
          <div className="space-y-3">
            {[
              { label: 'Calls Made', value: totalCallsMade, pct: 100, color: '#1E3A8A' },
              { label: 'Connected', value: totalCallsConnected, pct: totalCallsMade ? f2((totalCallsConnected / totalCallsMade) * 100) : 0, color: '#0F766E' },
              { label: 'Responded', value: totalResponses, pct: totalCallsMade ? f2((totalResponses / totalCallsMade) * 100) : 0, color: '#059669' },
            ]?.map((item) => (
              <div key={item?.label}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">{item?.label}</span>
                  <span className="font-medium data-text text-foreground">{item?.value?.toLocaleString()} <span className="text-muted-foreground">({item?.pct}%)</span></span>
                </div>
                <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${item?.pct}%`, background: item?.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Response Rate */}
        <div
          className="bg-card border border-border rounded-xl p-6"
          style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Response Rate</p>
              <div className="flex items-end gap-1">
                <span className="text-4xl font-bold data-text" style={{ color: responseRate >= 50 ? '#059669' : responseRate >= 30 ? '#D97706' : '#DC2626' }}>
                  {f2(responseRate)}%
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">Responses / Connected calls</p>
            </div>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: responseRate >= 50 ? '#d1fae5' : responseRate >= 30 ? '#fef3c7' : '#fee2e2' }}>
              <Icon name="Activity" size={20} color={responseRate >= 50 ? '#059669' : responseRate >= 30 ? '#D97706' : '#DC2626'} />
            </div>
          </div>

          {responseRate < 30 && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-red-50 border border-red-100 mb-3">
              <Icon name="AlertCircle" size={14} color="#DC2626" />
              <p className="text-xs text-red-700">Low response rate may affect data reliability</p>
            </div>
          )}

          {/* Circular progress */}
          <div className="flex items-center justify-center">
            <div className="relative w-20 h-20">
              <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
                <circle cx="40" cy="40" r="32" fill="none" stroke="#E5E7EB" strokeWidth="8" />
                <circle
                  cx="40" cy="40" r="32" fill="none"
                  stroke={responseRate >= 50 ? '#059669' : responseRate >= 30 ? '#D97706' : '#DC2626'}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${(responseRate / 100) * 201} 201`}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-bold data-text text-foreground">{f2(responseRate)}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>}
    </div>
  );
};

export default NPSKPICards;
